import React, { useMemo, useState } from 'react';
import '../PackageManagement.css';
import '../../components/packages/package-dialogs.css';
import './package-form-page.css';
import PackageFeatureLibraryPanel from './feature-library/PackageFeatureLibraryPanel';
import { buildFeatureLibrary, PACKAGE_FEATURE_DRAG_TYPE } from './feature-library/collectPackageFeatures';
import { slugFromPackageName } from './packageFormConstants';

const DeleteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const AddIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const DragIndicatorIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="9" cy="5" r="1.5" />
    <circle cx="9" cy="12" r="1.5" />
    <circle cx="9" cy="19" r="1.5" />
    <circle cx="15" cy="5" r="1.5" />
    <circle cx="15" cy="12" r="1.5" />
    <circle cx="15" cy="19" r="1.5" />
  </svg>
);

const PackageFormFields = ({
  formData,
  setFormData,
  isSuperAdmin,
  allPackages = [],
  currentPackageId = null,
  draggedFeatureIndex,
  dragOverFeatureIndex,
  onFeatureDragStart,
  onFeatureDragOver,
  onFeatureDrop,
  onFeatureDragEnd,
  onLibraryFeatureDrop,
  onSelectLibraryFeature,
  onAddCustomFeature,
}) => {
  const [featureLibraryOpen, setFeatureLibraryOpen] = useState(false);
  const [featuresDragOver, setFeaturesDragOver] = useState(false);
  const [slugOpen, setSlugOpen] = useState(false);
  const [slugEdited, setSlugEdited] = useState(() => {
    const currentSlug = String(formData.slug || '').trim();
    return Boolean(currentSlug) && currentSlug !== slugFromPackageName(formData.name);
  });

  const autoSlug = slugFromPackageName(formData.name);
  const slugPreview = formData.slug || autoSlug;
  const unlimitedEmployees = Number(formData.limits.employees) === -1;
  const isAdminPackage = Boolean(formData.isAdminPackage);

  const libraryGroups = useMemo(
    () => buildFeatureLibrary(allPackages, {
      excludePackageId: currentPackageId,
      currentFeatures: formData.features,
    }),
    [allPackages, currentPackageId, formData.features]
  );

  const updateForm = (patch) => setFormData({ ...formData, ...patch });

  const handleFeatureItemDrop = (event, index) => {
    if (event.dataTransfer.types.includes(PACKAGE_FEATURE_DRAG_TYPE)) {
      onLibraryFeatureDrop(event, index);
      setFeaturesDragOver(false);
      return;
    }
    onFeatureDrop(index);
  };

  const handleFeaturesSectionDragOver = (event) => {
    if (event.dataTransfer.types.includes(PACKAGE_FEATURE_DRAG_TYPE)) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
      setFeaturesDragOver(true);
    }
  };

  const handleFeaturesSectionDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setFeaturesDragOver(false);
    }
  };

  const handleFeaturesSectionDrop = (event) => {
    if (event.dataTransfer.types.includes(PACKAGE_FEATURE_DRAG_TYPE)) {
      onLibraryFeatureDrop(event);
      setFeaturesDragOver(false);
    }
  };

  const setAdminPackage = (nextAdmin) => {
    updateForm({
      isAdminPackage: nextAdmin,
      isTrialPackage: false,
      price: nextAdmin ? { monthly: 0, annually: 0 } : formData.price,
    });
  };

  return (
    <div className="pkg-form-page__sections">
      <section className="pkg-form-page__section admin-card">
        <div className="pkg-form-page__section-head">
          <h2 className="pkg-form-page__section-title">Package</h2>
          <p className="pkg-form-page__section-copy">Name it, describe it, and choose who it is for.</p>
        </div>

        {isSuperAdmin && (
          <div className="pkg-form-page__type-grid" role="group" aria-label="Package type">
            <button
              type="button"
              className={`pkg-form-page__type-card${!isAdminPackage ? ' is-selected' : ''}`}
              onClick={() => setAdminPackage(false)}
            >
              <strong>Catalog</strong>
              <span>Shown on the website with pricing</span>
            </button>
            <button
              type="button"
              className={`pkg-form-page__type-card${isAdminPackage ? ' is-selected' : ''}`}
              onClick={() => setAdminPackage(true)}
            >
              <strong>Internal admin</strong>
              <span>Permanent access, no price</span>
            </button>
          </div>
        )}

        <div className="pkg-form-group">
          <label className="pkg-label" htmlFor="package-name">Name</label>
          <input
            id="package-name"
            className="pkg-input"
            type="text"
            placeholder="Business Plus"
            value={formData.name}
            onChange={(e) => {
              const name = e.target.value;
              updateForm({
                name,
                slug: slugEdited ? formData.slug : slugFromPackageName(name),
              });
            }}
          />
          <div className="pkg-form-page__slug-row">
            <span>Slug: {slugPreview || '—'}</span>
            <button type="button" className="pkg-form-page__text-btn" onClick={() => setSlugOpen((open) => !open)}>
              {slugOpen ? 'Hide' : 'Edit'}
            </button>
          </div>
        </div>

        {slugOpen && (
          <div className="pkg-form-group">
            <label className="pkg-label" htmlFor="package-slug">Slug</label>
            <input
              id="package-slug"
              className="pkg-input"
              type="text"
              placeholder={autoSlug || 'auto from name'}
              value={formData.slug}
              onChange={(e) => {
                setSlugEdited(true);
                updateForm({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') });
              }}
            />
          </div>
        )}

        <div className="pkg-form-group pkg-form-group--last">
          <label className="pkg-label" htmlFor="package-description">Description</label>
          <textarea
            id="package-description"
            className="pkg-textarea"
            rows={2}
            placeholder="Short summary shown on the catalog card"
            value={formData.description}
            onChange={(e) => updateForm({ description: e.target.value })}
          />
        </div>

        <label className="pkg-dialog-check" htmlFor="package-active">
          <input
            id="package-active"
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) => updateForm({ isActive: e.target.checked })}
          />
          Active
        </label>
      </section>

      {!isAdminPackage && (
        <section className="pkg-form-page__section admin-card">
          <div className="pkg-form-page__section-head">
            <h2 className="pkg-form-page__section-title">Price and billing</h2>
            <p className="pkg-form-page__section-copy">What customers pay and how they can be billed.</p>
          </div>
          <div className="pkg-form-row">
            <div className="pkg-form-group">
              <label className="pkg-label" htmlFor="monthly-price">Monthly (USD)</label>
              <input
                id="monthly-price"
                className="pkg-input"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={formData.price?.monthly}
                onChange={(e) => updateForm({
                  price: { ...formData.price, monthly: e.target.value },
                })}
              />
            </div>
            <div className="pkg-form-group">
              <label className="pkg-label" htmlFor="annual-price">Annual (USD)</label>
              <input
                id="annual-price"
                className="pkg-input"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={formData.price?.annually}
                onChange={(e) => updateForm({
                  price: { ...formData.price, annually: e.target.value },
                })}
              />
            </div>
            <div className="pkg-form-group">
              <label className="pkg-label" htmlFor="annual-discount">Annual discount (%)</label>
              <input
                id="annual-discount"
                className="pkg-input"
                type="number"
                min="0"
                max="100"
                value={formData.subscription?.annualDiscount}
                onChange={(e) => {
                  const value = Math.min(100, Math.max(0, Number(e.target.value)));
                  updateForm({
                    subscription: { ...formData.subscription, annualDiscount: value },
                  });
                }}
              />
            </div>
          </div>
          <div className="pkg-form-page__chip-row">
            <label className="pkg-form-page__chip" htmlFor="billing-monthly">
              <input
                id="billing-monthly"
                type="checkbox"
                checked={formData.subscription?.billingOptions?.includes('monthly')}
                onChange={(e) => {
                  const currentOptions = formData.subscription?.billingOptions || [];
                  const newOptions = e.target.checked
                    ? [...currentOptions, 'monthly']
                    : currentOptions.filter((opt) => opt !== 'monthly');
                  updateForm({
                    subscription: { ...formData.subscription, billingOptions: newOptions },
                  });
                }}
              />
              Monthly billing
            </label>
            <label className="pkg-form-page__chip" htmlFor="billing-annually">
              <input
                id="billing-annually"
                type="checkbox"
                checked={formData.subscription?.billingOptions?.includes('annually')}
                onChange={(e) => {
                  const currentOptions = formData.subscription?.billingOptions || [];
                  const newOptions = e.target.checked
                    ? [...currentOptions, 'annually']
                    : currentOptions.filter((opt) => opt !== 'annually');
                  updateForm({
                    subscription: { ...formData.subscription, billingOptions: newOptions },
                  });
                }}
              />
              Annual billing
            </label>
            <label className="pkg-form-page__chip" htmlFor="auto-renew">
              <input
                id="auto-renew"
                type="checkbox"
                checked={formData.subscription?.autoRenew}
                onChange={(e) => updateForm({
                  subscription: { ...formData.subscription, autoRenew: e.target.checked },
                })}
              />
              Auto renew
            </label>
          </div>
          <div className="pkg-form-group pkg-form-group--last" style={{ marginTop: 16 }}>
            <label className="pkg-label" htmlFor="grace-period">Grace period (days)</label>
            <input
              id="grace-period"
              className="pkg-input"
              type="number"
              min="0"
              value={formData.subscription?.gracePeriodDays}
              onChange={(e) => {
                const value = e.target.value === '' ? 0 : Number(e.target.value);
                updateForm({
                  subscription: { ...formData.subscription, gracePeriodDays: value },
                });
              }}
            />
            <div className="pkg-helper-text">Days after a missed payment before access ends</div>
          </div>
        </section>
      )}

      <section className="pkg-form-page__section admin-card">
        <div className="pkg-form-page__section-head">
          <h2 className="pkg-form-page__section-title">Employees</h2>
          <p className="pkg-form-page__section-copy">How many staff this plan includes.</p>
        </div>
        <div className="pkg-form-page__limit-row">
          <input
            id="employees"
            className="pkg-input pkg-form-page__limit-input"
            type="number"
            min="-1"
            step="1"
            disabled={unlimitedEmployees}
            value={unlimitedEmployees ? -1 : formData.limits.employees}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '' || raw === '-') {
                updateForm({ limits: { ...formData.limits, employees: raw } });
                return;
              }
              const value = Number(raw);
              if (!Number.isFinite(value)) return;
              updateForm({
                limits: { ...formData.limits, employees: value < 0 ? -1 : Math.trunc(value) },
              });
            }}
          />
          <label className="pkg-form-page__chip" htmlFor="unlimited-employees">
            <input
              id="unlimited-employees"
              type="checkbox"
              checked={unlimitedEmployees}
              onChange={(e) => updateForm({
                limits: { ...formData.limits, employees: e.target.checked ? -1 : 1 },
              })}
            />
            Unlimited
          </label>
        </div>
      </section>

      <section
        className={`pkg-form-page__section admin-card pkg-features-section${
          featuresDragOver ? ' pkg-features-section--drag-over' : ''
        }`}
        onDragOver={handleFeaturesSectionDragOver}
        onDragLeave={handleFeaturesSectionDragLeave}
        onDrop={handleFeaturesSectionDrop}
      >
        <div className="pkg-form-page__section-head">
          <h2 className="pkg-form-page__section-title">Features</h2>
          <p className="pkg-form-page__section-copy">Add selling points shown on the package card.</p>
        </div>
        {formData.features.length === 0 ? (
          <div className="pkg-feature-drop-zone">No features yet. Add one or drag from the library.</div>
        ) : (
          formData.features.map((feature, index) => (
            <div
              key={index}
              className={`pkg-feature-item ${
                draggedFeatureIndex === index ? 'pkg-feature-item-dragging' : ''
              } ${dragOverFeatureIndex === index ? 'pkg-feature-item-drag-over' : ''}`}
              onDragOver={(event) => onFeatureDragOver(event, index)}
              onDrop={(event) => handleFeatureItemDrop(event, index)}
            >
              <div
                className="pkg-drag-handle"
                draggable
                onDragStart={(event) => onFeatureDragStart(index, event)}
                onDragEnd={onFeatureDragEnd}
              >
                <DragIndicatorIcon />
              </div>
              <input
                className="pkg-input"
                type="text"
                placeholder={`Feature ${index + 1}`}
                value={feature}
                onChange={(e) => {
                  const newFeatures = [...formData.features];
                  newFeatures[index] = e.target.value;
                  updateForm({ features: newFeatures });
                }}
              />
              <button
                type="button"
                className="pkg-btn-icon pkg-btn-icon-danger"
                onClick={() => {
                  updateForm({ features: formData.features.filter((_, i) => i !== index) });
                }}
              >
                <DeleteIcon />
              </button>
            </div>
          ))
        )}
        <button
          type="button"
          className="pkg-btn pkg-btn-secondary"
          onClick={() => setFeatureLibraryOpen(true)}
        >
          <AddIcon />
          Add feature
        </button>
      </section>

      <PackageFeatureLibraryPanel
        open={featureLibraryOpen}
        onClose={() => setFeatureLibraryOpen(false)}
        libraryGroups={libraryGroups}
        onSelectFeature={onSelectLibraryFeature}
        onAddCustomFeature={() => {
          onAddCustomFeature();
          setFeatureLibraryOpen(false);
        }}
      />
    </div>
  );
};

export default PackageFormFields;
